import { StowageRuleId } from 'src/enums/stowage/stowageRule/stowageRuleId';
import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { StowageRuleViolationStatus } from 'src/enums/stowage/stowageRule/stowageRuleViolationStatus';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { ViolationGroup } from 'src/types/ViewModel/Stowage/Violation/ViolationGroup';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { getBerthCalls } from '../stowageRotationUtils';

export const violationGroupPolicies = new Map<StowageRuleId, 'sequence' | 'blocked' | 'operation'>([
    [StowageRuleId.MaxAdjacentTemperatureSoftRule, 'sequence'],
    [StowageRuleId.BlockedTankSoftRule, 'blocked'],
    [StowageRuleId.CoatingCompatibilitySoftRule, 'operation'],
    [StowageRuleId.FillRatioOver98PercentSoftRule, 'operation'],
    [StowageRuleId.Ibc15_11_3SoftRule, 'operation'],
    [StowageRuleId.Ibc15_14SoftRule, 'operation'],
    [StowageRuleId.IncompatibleHeatingMediumSoftRule, 'operation'],
    [StowageRuleId.InsufficientHeatingCapacitySoftRule, 'operation'],
    [StowageRuleId.TankMaxLitreWeightExceededSoftRule, 'operation'],
    [StowageRuleId.PropyleneOxideSoftRule, 'operation'],
    [StowageRuleId.UsedCookingOilSoftRule, 'operation'],
    [StowageRuleId.BasfHmdSoftRule, 'operation'],
    [StowageRuleId.OxygenSensitiveProductsSoftRule, 'operation'],
]);

export const assignViolationGroups = (violations: Violation[], plan: StowagePlan): void => {
    const berths = plan.rotation.flatMap(getBerthCalls);
    const berthOrder = new Map(berths.map((berth, index) => [berth.berthCallGuid, index]));
    const candidates = new Map<string, Violation[]>();
    const occupancyEpisodes = new Map<string, Map<string, string>>();
    const occupancyAnchor = (violation: Violation): string | undefined => {
        const cargoKey = `${violation.tankId}-${violation.cargoGuid}`;
        if (!occupancyEpisodes.has(cargoKey)) {
            const anchors = new Map<string, string>();
            let anchor: string | undefined;
            for (const snapshot of plan.manifest?.cargoSequenceSnapshots ?? []) {
                const phases = [snapshot.arrival, snapshot.during, snapshot.departure];
                phases.forEach((contents, phase) => {
                    const occupied = contents
                        .get(violation.tankId)
                        ?.cargoContents.some((content) => content.cargo.cargoGuid === violation.cargoGuid && content.weight.gt(0));
                    if (!occupied) anchor = undefined;
                    else if (!anchor)
                        anchor = JSON.stringify([snapshot.berth.berthCallGuid, snapshot.operations.map((op) => op.cargoOperationGuid), phase]);
                });
                if (anchor) anchors.set(snapshot.berth.berthCallGuid, anchor);
                else anchors.delete(snapshot.berth.berthCallGuid);
            }
            occupancyEpisodes.set(cargoKey, anchors);
        }
        return occupancyEpisodes.get(cargoKey).get(violation.berthCallGuid);
    };

    for (const violation of violations) {
        const policy = violationGroupPolicies.get(violation.stowageRuleId);
        if (violation.type !== StowageRuleConsequenceType.SoftRule || !policy || !violation.ruleEvaluation || !violation.tankId) continue;
        if (!berthOrder.has(violation.berthCallGuid)) continue;
        if (policy === 'sequence' && violation.ruleEvaluation.position === undefined) continue;
        if (policy === 'operation' && !violation.cargoOperationGuid) continue;
        if (policy !== 'sequence') violation.ruleEvaluation.position = berthOrder.get(violation.berthCallGuid);
        const blockedOccupancy = policy === 'blocked' ? occupancyAnchor(violation) : undefined;
        if (policy === 'blocked' && !blockedOccupancy) continue;
        const identity = JSON.stringify([
            violation.stowageRuleId,
            violation.cargoGuid,
            violation.tankId,
            violation.adjacentTankId,
            violation.sequenceConflictKey,
            policy === 'operation' ? violation.cargoOperationGuid : null,
            violation.ruleEvaluation.inputs,
            blockedOccupancy,
            policy === 'blocked'
                ? (plan.blockedTanks ?? [])
                      .filter((block) => block.tankId === violation.tankId)
                      .map((block) => [
                          block.blockedTankGuid,
                          block.fromBerthCallGuid,
                          block.toBerthCallGuid,
                          block.fromPortCallGuid,
                          block.toPortCallGuid,
                      ])
                      .sort()
                : null,
        ]);
        const group = candidates.get(identity) ?? [];
        group.push(violation);
        candidates.set(identity, group);
    }

    for (const [identity, members] of Array.from(candidates)) {
        const policy = violationGroupPolicies.get(members[0].stowageRuleId);
        const position = (violation: Violation) =>
            policy === 'sequence' ? violation.ruleEvaluation.position : berthOrder.get(violation.berthCallGuid);
        members.sort((a, b) => position(a) - position(b));
        let episode: Violation[] = [];
        const finish = () => {
            if (!episode.length) return;
            const anchor = episode[0].ruleEvaluation.key ?? episode[0].berthCallGuid;
            const key = JSON.stringify(['violation-group-v1', identity, anchor]);
            for (const member of episode) {
                member.groupKey = key;
            }
        };
        for (const member of members) {
            const previous = episode[episode.length - 1];
            // Missing evaluations are not evidence of continuity.
            if (previous && position(member) - position(previous) > 1) {
                finish();
                episode = [];
            }
            episode.push(member);
        }
        finish();
    }
};

export const getViolationGroups = (violations: Violation[]): ViolationGroup[] => {
    const groups = new Map<string, Violation[]>();
    for (const violation of violations ?? []) {
        const key = violation.groupKey ?? violation.violationGuid;
        const members = groups.get(key) ?? [];
        members.push(violation);
        groups.set(key, members);
    }
    return Array.from(groups, ([key, members]) => {
        if (members.every((member) => member.ruleEvaluation?.position !== undefined)) {
            members.sort((a, b) => a.ruleEvaluation.position - b.ruleEvaluation.position);
        }
        const acceptedCount = members.filter((member) => member.status === StowageRuleViolationStatus.Accepted).length;
        return {
            key,
            members,
            berthCount: new Set(members.map((member) => member.berthCallGuid).filter(Boolean)).size,
            acceptedCount,
            status: acceptedCount === members.length ? 'Accepted' : acceptedCount ? 'Partially accepted' : 'Needs acceptance',
        };
    });
};

export const getShownViolationGroups = (violations: Violation[], filtered: Violation[], statusFilter: string): ViolationGroup[] => {
    const visible = new Set((filtered ?? []).map((violation) => violation.violationGuid));
    return getViolationGroups(violations)
        .filter((group) => {
            if (!group.members.some((member) => visible.has(member.violationGuid))) return false;
            if (group.members[0].type !== StowageRuleConsequenceType.SoftRule || statusFilter === 'All statuses') return true;
            return statusFilter === 'Accepted' ? group.status === 'Accepted' : group.status !== 'Accepted';
        })
        .sort((a, b) => Number(a.status === 'Accepted') - Number(b.status === 'Accepted'));
};
